import React from "react";
import { useLocation, useNavigate } from "react-router";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";

import type {
    JourneyFeedbackMetadata,
    JourneyInstanceCreatePayloadJson,
    JourneyInstanceDTO,
    JourneyInstanceMetadata,
    JourneyStepEventCreatePayload,
    JourneyStepType,
} from "@volley/data";

import logger from "../../log";
import { fetchApi } from "../../util";
import { logFetchError } from "../../util/fetchApi";
import LowerHeadDialog from "../Dialog/LowerHeadDialog";
import JourneyFeedbackDialog from "../Journeys/JourneyFeedbackDialog";
import { inProgressUrl } from "../Journeys/journeyNav";
import { useSelectedSport } from "../common/context/sport";

import { usePairingContext } from "./pairingStatus";

type JourneyAppWorkout = JourneyInstanceDTO["workoutsPlays"][number];

type JourneyValue = Readonly<{
    activeJourneys: JourneyInstanceDTO[];
    refreshActiveJourneys: (
        type?: "class" | "assessment",
    ) => Promise<JourneyInstanceDTO[]>;
    /** Advance the journey past the workout that just ended. */
    onWorkoutFinished: (workoutId: number) => void;
    getJourneyWorkout: (workoutId: number) => JourneyAppWorkout | null;
    startJourney: (
        id: number,
        metadata?: JourneyInstanceMetadata,
    ) => Promise<{ id: number; ulid: string }>;
    /** Resolves false when the play could not be linked to the journey. */
    registerJourneyLinkIntent: (
        instanceUlid: string,
        appWorkoutId: number,
    ) => Promise<boolean>;
    markBallThrown: (workoutId: number) => void;
    logStepEvent: (
        stepType: JourneyStepType,
        options?: {
            journeyInstanceUlid?: string;
            journeyId?: number;
            metadata?: Record<string, unknown>;
        },
    ) => void;
}>;

export const JourneyContext = React.createContext<JourneyValue>({
    activeJourneys: [],
    refreshActiveJourneys: () => Promise.resolve([]),
    onWorkoutFinished: () => {},
    getJourneyWorkout: () => null,
    startJourney: () => Promise.resolve({ id: 0, ulid: "" }),
    registerJourneyLinkIntent: () => Promise.resolve(false),
    markBallThrown: () => {},
    logStepEvent: () => {},
});

interface PendingFeedback {
    journeyType: "assessment" | "class";
    metadata: JourneyFeedbackMetadata;
}

const FEEDBACK_PENDING_KEY = "JOURNEY_FEEDBACK_PENDING";
const FEEDBACK_SESSION_SHOWN_KEY = "JOURNEY_FEEDBACK_SESSION_SHOWN";
const FEEDBACK_TRIGGER_PATHS = new Set([
    "/",
    "/journeys/classes",
    "/journeys/assessments",
]);

// URL param that opts a play session out of all journey behavior, even if the
// workout is part of an active journey. Used when entering a workout as
// standalone practice from surfaces like PlayStartDialog.
const FORCE_NO_JOURNEY_PARAM = "forceNoJourney";

// Tracks which (journey instance, workout) pair the current/upcoming play
// belongs to. Set when registerJourneyLinkIntent succeeds.
const PENDING_JOURNEY_PLAY_KEY = "JOURNEY_PENDING_PLAY_CONTEXT";

interface PendingJourneyPlay {
    instanceUlid: string;
    appWorkoutId: number;
}

export const JourneyProvider = ({ children }: React.PropsWithChildren) => {
    const { selected: selectedSport } = useSelectedSport();
    const { sessionId: pairingSessionId } = usePairingContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeJourneys, setActiveJourneys] = React.useState<
        JourneyInstanceDTO[]
    >([]);
    const [lowerHeadDialogOpen, setLowerHeadDialogOpen] = React.useState(false);
    const [lowered, setLowered] = React.useState(false);
    const [pendingFeedback, setPendingFeedback] =
        React.useState<PendingFeedback | null>(null);
    const [feedbackDialogOpen, setFeedbackDialogOpen] = React.useState(false);
    const [pendingJourneyPlay, setPendingJourneyPlayState] =
        React.useState<PendingJourneyPlay | null>(() => {
            try {
                const saved = sessionStorage.getItem(PENDING_JOURNEY_PLAY_KEY);
                return saved ? (JSON.parse(saved) as PendingJourneyPlay) : null;
            } catch {
                return null;
            }
        });

    const setPendingJourneyPlay = React.useCallback(
        (next: PendingJourneyPlay | null) => {
            setPendingJourneyPlayState(next);
            if (next) {
                sessionStorage.setItem(
                    PENDING_JOURNEY_PLAY_KEY,
                    JSON.stringify(next),
                );
            } else {
                sessionStorage.removeItem(PENDING_JOURNEY_PLAY_KEY);
            }
        },
        [],
    );

    const forceNoJourney = React.useMemo(
        () => new URLSearchParams(location.search).has(FORCE_NO_JOURNEY_PARAM),
        [location.search],
    );

    const onLowered = React.useCallback(() => setLowered(true), []);

    const onCloseLowerHeadDialog = React.useCallback(
        () => setLowerHeadDialogOpen(false),
        [],
    );

    const refreshActiveJourneys = React.useCallback(
        async (type?: "class" | "assessment") => {
            const url = new URL(
                "/api/journeys/instances/mine/active",
                window.location.origin,
            );
            url.searchParams.set("sport", selectedSport);
            if (type) {
                url.searchParams.set("type", type);
            }

            try {
                const result = await fetchApi<JourneyInstanceDTO[]>(
                    url.toString(),
                    "GET",
                );
                setActiveJourneys(result);
                return result;
            } catch (err) {
                logFetchError(err);
                return [];
            }
        },
        [selectedSport],
    );

    React.useEffect(() => {
        refreshActiveJourneys().catch(logFetchError);
    }, [refreshActiveJourneys]);

    // Show feedback dialog when user navigates to a trigger path
    // and a pending feedback flag was set by markBallThrown
    React.useEffect(() => {
        if (!FEEDBACK_TRIGGER_PATHS.has(location.pathname)) return;

        const pendingJson = localStorage.getItem(FEEDBACK_PENDING_KEY);
        if (!pendingJson) return;

        let pending: PendingFeedback;
        try {
            pending = JSON.parse(pendingJson) as PendingFeedback;
        } catch {
            localStorage.removeItem(FEEDBACK_PENDING_KEY);
            return;
        }

        // Clear pending flag regardless of whether we show the dialog
        localStorage.removeItem(FEEDBACK_PENDING_KEY);

        // Only prompt once per trainer session
        if (localStorage.getItem(FEEDBACK_SESSION_SHOWN_KEY)) return;

        localStorage.setItem(FEEDBACK_SESSION_SHOWN_KEY, "1");
        setPendingFeedback(pending);
        setFeedbackDialogOpen(true);
    }, [location.pathname]);

    const findCurrentPlayJourney = React.useCallback(
        (workoutId: number) => {
            if (
                !pendingJourneyPlay ||
                pendingJourneyPlay.appWorkoutId !== workoutId
            ) {
                return null;
            }
            return (
                activeJourneys.find(
                    (j) => j.ulid === pendingJourneyPlay.instanceUlid,
                ) ?? null
            );
        },
        [pendingJourneyPlay, activeJourneys],
    );

    const markBallThrown = React.useCallback(
        (workoutId: number) => {
            if (forceNoJourney) return;
            const activeJourney = findCurrentPlayJourney(workoutId);
            if (!activeJourney) return;

            const workoutPlay = activeJourney.workoutsPlays.find(
                (p) => p.appWorkout.id === workoutId,
            );

            const pending: PendingFeedback = {
                journeyType: activeJourney.journey.type as
                    | "assessment"
                    | "class",
                metadata: {
                    journeyInstanceUlid: activeJourney.ulid,
                    journeyId: activeJourney.journey.id,
                    journeyTitle: activeJourney.journey.title,
                    completionStatus: "incomplete",
                    sport: activeJourney.journey.sportName,
                    lastWorkoutName: workoutPlay?.appWorkout.name,
                    sessionId: pairingSessionId,
                },
            };
            localStorage.setItem(FEEDBACK_PENDING_KEY, JSON.stringify(pending));
        },
        [findCurrentPlayJourney, pairingSessionId, forceNoJourney],
    );

    const registerJourneyLinkIntent = React.useCallback(
        async (instanceUlid: string, appWorkoutId: number) => {
            try {
                await fetchApi(
                    `/api/journeys/instances/${instanceUlid}/register-next-play`,
                    "POST",
                    { appWorkoutId },
                );
                setPendingJourneyPlay({ instanceUlid, appWorkoutId });
                return true;
            } catch (err) {
                // Best-effort for a user-initiated navigation: a missing intent
                // just means the play won't be linked to the journey. We log but
                // don't block. Callers that navigate on the player's behalf
                // (auto-advance) check the result instead, since an unlinked play
                // would silently drop that workout's clips from the assessment.
                logFetchError(err);
                return false;
            }
        },
        [setPendingJourneyPlay],
    );

    const onWorkoutFinished = React.useCallback(
        (workoutId: number) => {
            if (forceNoJourney) return;
            const activeJourney = findCurrentPlayJourney(workoutId);
            if (!activeJourney) return;

            const currentSequence = activeJourney.workoutsPlays.find(
                (p) => p.appWorkout.id === workoutId,
            )?.sequence;

            if (!currentSequence) {
                logger.warn(
                    `useJourney: workout '${workoutId}' not found in active journey '${activeJourney.id}'`,
                );
                return;
            }

            let nextPlay = null;
            let minNextSequence = Infinity;
            for (const p of activeJourney.workoutsPlays) {
                if (p.sequence > currentSequence) {
                    if (p.sequence < minNextSequence) {
                        minNextSequence = p.sequence;
                        nextPlay = p;
                    }
                }
            }

            const goToInProgress = () => {
                // Navigate synchronously, then refresh in the background.
                // The in-progress page does its own refresh on mount, so
                // gating navigation on the refresh promise just adds a race
                // where the .then() callback can be dropped on iOS during
                // visibility/tab-restoration transitions.
                navigate(
                    inProgressUrl({
                        journeyType: activeJourney.journey.type,
                        journeyInstanceId: activeJourney.id,
                        journeyInstanceUlid: activeJourney.ulid,
                    }),
                    { replace: true },
                );
                refreshActiveJourneys().catch(logFetchError);
            };

            if (nextPlay) {
                goToInProgress();
            } else if (
                activeJourney.hasPreviousCompletion &&
                activeJourney.journey.type === "class"
            ) {
                // Previously completed class - let user decide when to complete
                goToInProgress();
            } else {
                fetchApi(
                    `/api/journeys/instances/${activeJourney.ulid}/complete`,
                    "PUT",
                )
                    .then(() => {
                        // Remove the completed journey from active journeys
                        setActiveJourneys((prev) =>
                            prev.filter((j) => j.id !== activeJourney.id),
                        );
                        // Clear pending feedback — the done page
                        // handles its own feedback dialog
                        localStorage.removeItem(FEEDBACK_PENDING_KEY);
                        const typePath =
                            activeJourney.journey.type === "assessment"
                                ? "assessments"
                                : "classes";
                        navigate(
                            `/journeys/${typePath}/${activeJourney.ulid}/done`,
                            {
                                replace: true,
                            },
                        );
                    })
                    .catch(logFetchError);
            }
            setPendingJourneyPlay(null);
        },
        [
            findCurrentPlayJourney,
            refreshActiveJourneys,
            navigate,
            forceNoJourney,
            setPendingJourneyPlay,
        ],
    );

    const getJourneyWorkout = React.useCallback(
        (workoutId: number) => {
            if (forceNoJourney) return null;
            const journey = findCurrentPlayJourney(workoutId);
            if (!journey) return null;
            return (
                journey.workoutsPlays.find(
                    (w) => w.appWorkout.id === workoutId,
                ) ?? null
            );
        },
        [findCurrentPlayJourney, forceNoJourney],
    );

    const startJourney = React.useCallback(
        async (journeyId: number, metadata?: JourneyInstanceMetadata) => {
            const payload: JourneyInstanceCreatePayloadJson = {
                journeyId,
                metadata,
            };

            try {
                const result = await fetchApi<{
                    createdId: number;
                    createdUlid: string;
                }>("/api/journeys/instances", "POST", payload);
                await refreshActiveJourneys();
                return { id: result.createdId, ulid: result.createdUlid };
            } catch (err) {
                logFetchError(err);
                throw err;
            }
        },
        [refreshActiveJourneys],
    );

    /**
     * Fire-and-forget step event logging for drop-off analytics.
     * Silent failure - analytics should never impact user experience.
     */
    const logStepEvent = React.useCallback(
        (
            stepType: JourneyStepType,
            options?: {
                journeyInstanceUlid?: string;
                journeyId?: number;
                metadata?: Record<string, unknown>;
            },
        ) => {
            const payload: JourneyStepEventCreatePayload = {
                stepType,
                journeyInstanceUlid: options?.journeyInstanceUlid,
                journeyId: options?.journeyId,
                metadata: options?.metadata,
            };

            // Fire-and-forget: no await, silent error handling
            fetch("/api/journeys/step-events", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
                credentials: "include",
            }).catch(() => {
                // Silent failure - analytics should not block UX
            });
        },
        [],
    );

    const value = React.useMemo<JourneyValue>(
        () => ({
            activeJourneys,
            refreshActiveJourneys,
            onWorkoutFinished,
            getJourneyWorkout,
            startJourney,
            registerJourneyLinkIntent,
            markBallThrown,
            logStepEvent,
        }),
        [
            activeJourneys,
            refreshActiveJourneys,
            onWorkoutFinished,
            getJourneyWorkout,
            startJourney,
            registerJourneyLinkIntent,
            markBallThrown,
            logStepEvent,
        ],
    );

    return (
        <JourneyContext.Provider value={value}>
            {children}
            <Dialog open={lowerHeadDialogOpen} onClose={onCloseLowerHeadDialog}>
                {lowered ? (
                    <>
                        <DialogContent>
                            <DialogContentText>
                                Please confirm ball bin is filled for next
                                workout.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={onCloseLowerHeadDialog}
                            >
                                Confirm
                            </Button>
                        </DialogActions>
                    </>
                ) : (
                    <LowerHeadDialog
                        reason="refill"
                        message="The trainer arm is moving to a more convenient height for refilling the bin."
                        onLowered={onLowered}
                        onStopped={onCloseLowerHeadDialog}
                    />
                )}
            </Dialog>
            {pendingFeedback && (
                <JourneyFeedbackDialog
                    open={feedbackDialogOpen}
                    onClose={() => setFeedbackDialogOpen(false)}
                    journeyType={pendingFeedback.journeyType}
                    metadata={pendingFeedback.metadata}
                />
            )}
        </JourneyContext.Provider>
    );
};

const useJourney = () => React.useContext(JourneyContext);
export default useJourney;
